import { Box, Button } from "@chakra-ui/react"
import { IconName } from "@pathwright/pathicons"
import { AUGMENTATION_SECTION_FOCUS_SELECTOR } from "@pathwright/ux/src/components/augmentation/sectionFocus"
import Link, { LinkTo } from "@pathwright/ux/src/components/link/Link"
import Pathicon from "@pathwright/ux/src/components/pathicon/Pathicon"
import Tooltip from "@pathwright/ux/src/components/tooltip/Tooltip"
import { ReactNode, useMemo } from "react"
import { useLocation } from "react-router-dom"
import { useCoverPageContext } from "../CoverPageProvider"
import { useCoverPageIsEditable } from "../access"
import { COVER_PAGE_PADDING } from "../constants"
import { highlightCoverPageSection } from "../coverPageHashNavigation"
import { CoverPagePanelSectionKey } from "../types"
import { coverPageRevealOnHoverCss } from "../ui"
import { useCoverPageTranslate } from "../utils"
import { CoverPagePanelSection } from "./CoverPagePanelHome"
import { getCoverPagePanelSectionLinkTo } from "./utils"

type CoverPageSectionEditTriggerProps = {
  label: string | null
  ariaLabel?: string | null
  /** When set, the trigger navigates (design panel link). Otherwise a plain button. */
  linkTo?: LinkTo
  onClick?: () => void
  disabled?: boolean
  lockedMessage?: ReactNode
  isDirty?: boolean
  icon?: IconName
}

export function CoverPageSectionEditTriggerButton({
  label,
  ariaLabel = label,
  linkTo,
  onClick,
  disabled = false,
  lockedMessage,
  isDirty = false,
  icon = "edit"
}: CoverPageSectionEditTriggerProps) {
  const isLocked = !!lockedMessage
  const isDisabled = disabled || isLocked

  const triggerContent = (
    <>
      <Pathicon icon={icon} />
      {isDirty ? (
        <Box
          as="span"
          aria-hidden="true"
          color="brand.500"
          fontSize="sm"
          fontWeight="bold"
          lineHeight="1"
          ml={1}
        >
          *
        </Box>
      ) : null}
      {label && (
        <Box as="span" className="CoverPageSectionTriggerLabel">
          {label}
        </Box>
      )}
    </>
  )

  const button = (
    <Button
      asChild={!!linkTo}
      aria-label={ariaLabel ?? `Cover Page section edit link`}
      className="CoverPageSectionTriggerLink"
      gap={0}
      // @ts-expect-error: TODO: get typing correct using chakra typegen https://chakra-ui.com/docs/get-started/cli#chakra-typegen
      variant="blurredLightGray"
      h="32px"
      minW="32px"
      px={2}
      borderRadius="full"
      css={{
        "& .CoverPageSectionTriggerLabel": {
          display: "inline-block",
          maxWidth: 0,
          opacity: 0,
          overflow: "hidden",
          whiteSpace: "nowrap",
          marginLeft: 0,
          verticalAlign: "top",
          transition:
            "max-width 0.2s ease, opacity 0.2s ease, margin-left 0.2s ease"
        },
        [`[role='group']:hover & .CoverPageSectionTriggerLabel, &:hover .CoverPageSectionTriggerLabel, &:focus-visible .CoverPageSectionTriggerLabel, ${AUGMENTATION_SECTION_FOCUS_SELECTOR} & .CoverPageSectionTriggerLabel`]:
          {
            maxWidth: "220px",
            opacity: 1,
            marginLeft: "6px"
          }
      }}
      onClick={onClick}
      disabled={!linkTo && isDisabled}
      opacity={isLocked ? 0.65 : undefined}
      cursor={isLocked ? "not-allowed" : undefined}
      aria-disabled={isDisabled || undefined}
    >
      {linkTo ? (
        <Link
          to={linkTo}
          textDecor="none"
          _hover={{ textDecor: "none" }}
          aria-disabled={isDisabled || undefined}
          data-disabled={isDisabled ? "" : undefined}
          tabIndex={isDisabled ? -1 : undefined}
          onClick={(event) => {
            if (isDisabled) {
              event.preventDefault()
              event.stopPropagation()
            }
          }}
        >
          {triggerContent}
        </Link>
      ) : (
        triggerContent
      )}
    </Button>
  )

  if (isLocked) {
    return (
      <Tooltip title={lockedMessage} trigger="click">
        <Box as="span" display="inline-flex" pointerEvents="auto">
          {button}
        </Box>
      </Tooltip>
    )
  }

  return button
}

function useCoverPagePanelSections() {
  const { t } = useCoverPageTranslate()
  const canPreview = useCoverPageContext((value) => value.canPreview)
  const offers = useCoverPageContext((value) => value.offers)
  const contributors = useCoverPageContext((value) => value.contributors)
  const categories = useCoverPageContext((value) => value.categories)

  return useMemo<CoverPagePanelSection[]>(
    () => [
      {
        sectionKey: "header",
        label: t("panel_sections.header", { defaultValue: "Header" }),
        heading: t("panel_sections.header", { defaultValue: "Header" }),
        triggerLabel: t("panel_sections.header", { defaultValue: "Header" })
      },
      {
        sectionKey: "preview",
        label: t("panel_sections.preview", { defaultValue: "Preview" }),
        heading: t("panel_sections.preview", { defaultValue: "Preview" }),
        triggerLabel: canPreview
          ? t("panel_sections.preview", { defaultValue: "Preview" })
          : t("panel_sections.add_steps_to_preview", {
              defaultValue: "Add steps to preview"
            })
      },
      {
        sectionKey: "offers",
        label: t("panel_sections.offers", { defaultValue: "Offers" }),
        heading: t("panel_sections.offers", { defaultValue: "Offers" }),
        triggerLabel: offers?.length
          ? t("panel_sections.offers", { defaultValue: "Offers" })
          : t("panel_sections.add_offers", { defaultValue: "Add offers" })
      },
      {
        sectionKey: "contributors",
        label: t("panel_sections.contributors", {
          defaultValue: "Contributors"
        }),
        heading: t("panel_sections.contributors", {
          defaultValue: "Contributors"
        }),
        triggerLabel: contributors?.length
          ? t("panel_sections.contributors", { defaultValue: "Contributors" })
          : t("panel_sections.add_contributor", {
              defaultValue: "Add contributor"
            })
      },
      {
        sectionKey: "categories",
        label: t("panel_sections.categories", { defaultValue: "Categories" }),
        heading: t("panel_sections.categories", { defaultValue: "Categories" }),
        triggerLabel: categories?.length
          ? t("panel_sections.categories", { defaultValue: "Categories" })
          : t("panel_sections.add_category", { defaultValue: "Add category" })
      }
    ],
    [canPreview, categories?.length, contributors?.length, offers?.length, t]
  )
}

function CoverPageSectionTrigger({
  sectionKey,
  top = 4,
  includeLabel = true,
  right,
  linkTo,
  panelItemId,
  preventScrollHighlight = false,
  revealWithin,
  itemName
}: {
  sectionKey: CoverPagePanelSectionKey
  top?: number | string
  includeLabel?: boolean
  right?: number | string
  /** Override navigation target (e.g. manage author URL). Defaults to panel + scroll hash. */
  linkTo?: LinkTo
  /** Offer panel route id, e.g. `668` → `/cover/668`. */
  panelItemId?: number
  /** Skip scrolling to the section when the trigger is clicked. */
  preventScrollHighlight?: boolean
  /**
   * Selector for the container whose hover or focus reveals this trigger,
   * e.g. `.CoverPageOfferCard` for one item's trigger. Defaults to the
   * section (`role="group"`) the trigger is a direct child of.
   */
  revealWithin?: string
  /**
   * The one item this trigger edits, e.g. an Offer's title. Names the control
   * "Edit {item}" so several triggers in a section are told apart.
   */
  itemName?: string | null
}) {
  const { pathname } = useLocation()
  const isEditable = useCoverPageIsEditable()
  const immersiveActive = useCoverPageContext((value) => value.immersiveActive)
  const sections = useCoverPagePanelSections()
  const { t } = useCoverPageTranslate()

  const section = sections.find((value) => value.sectionKey === sectionKey)
  const sectionEditLinkTo =
    linkTo ?? getCoverPagePanelSectionLinkTo(pathname, sectionKey, panelItemId)

  if (!isEditable || immersiveActive || !section) return null

  return (
    <Box
      position="absolute"
      top={top}
      right={right ?? COVER_PAGE_PADDING}
      zIndex={11}
      pointerEvents="auto"
      // Shown on hover or focus of its section or item, not all the time.
      css={coverPageRevealOnHoverCss(
        revealWithin
          ? `${revealWithin}:hover &, ${revealWithin}:focus-within &`
          : "[role='group']:hover > &, [role='group']:focus-within > &"
      )}
    >
      <CoverPageSectionEditTriggerButton
        label={includeLabel ? section.triggerLabel : null}
        // Say what it edits: the section, or the one item within it.
        ariaLabel={t("panel_sections.edit_named", {
          defaultValue: "Edit {{name}}",
          name: itemName?.trim() || section.heading
        })}
        linkTo={sectionEditLinkTo}
        onClick={() => {
          if (!preventScrollHighlight) {
            highlightCoverPageSection(sectionKey, { highlightMain: false })
          }
        }}
      />
    </Box>
  )
}

export default CoverPageSectionTrigger
